// Staff sign-in at /admin/sign-in: password, first-login new password, TOTP setup, TOTP code,
// and forgot-password, all in-page against Cognito (lib/auth). Dev mode skips straight through.
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Circle, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { FirmHeader, FirmPage } from '../firm/FirmHeader';
import {
  APP_NAME, AuthError, PASSWORD_RULES, confirmPasswordReset, currentSession, isPlatformAdmin, passwordMeetsPolicy,
  requestPasswordReset, signOut, startSignIn, submitMfaSetupCode, submitNewPassword, submitTotp, type SignInStep,
} from '../lib/auth';
import { loadConfig } from '../lib/config';
import { Banner, Button, PasswordField, Spinner, TextField } from '../ui';

/** Only same-app admin paths; anything else lands on /admin. */
export function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith('/admin') || raw.startsWith('//') || raw.startsWith('/admin/sign-in')) return '/admin';
  return raw;
}

export const signInPath = (next: string) => `/admin/sign-in?next=${encodeURIComponent(next)}`;

/** Navy brand header and one centered card, as on the firm message pages. */
export function AuthCard({ title, caption, children, wide }: { title: string; caption?: ReactNode; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLHeadingElement>(null);
  // When a step mounts without focusing a field, focus its heading so screen readers announce it.
  useEffect(() => { document.title = APP_NAME; }, []);
  useEffect(() => {
    const a = document.activeElement;
    if (!a || a === document.body) ref.current?.focus();
  }, [title]);
  return (
    <FirmPage>
      <FirmHeader subtitle="Platform Manager" staff />
      <main className={`mx-auto px-4 py-10 sm:px-6 sm:py-14 ${wide ? 'max-w-[560px]' : 'max-w-[480px]'}`}>
        <section className="rounded-2xl border border-line bg-white p-6 shadow-card sm:p-8">
          <h1 ref={ref} tabIndex={-1} className="mb-2 mt-0 text-[26px] font-bold leading-[1.2] tracking-[-0.02em] outline-none">{title}</h1>
          {caption && <p className="mb-6 mt-0 text-ink-2">{caption}</p>}
          <div className="flex flex-col gap-4">{children}</div>
        </section>
      </main>
    </FirmPage>
  );
}

function ErrorBanner({ error }: { error: string | null }) {
  return error ? <Banner tone="error" role="alert">{error}</Banner> : null;
}

const message = (e: unknown) => (e instanceof AuthError ? e.message : 'Something went wrong. Try again.');

/** Live checklist of the pool's password rules. */
function PasswordRules({ id, password, confirm }: { id: string; password: string; confirm?: string }) {
  const rows = PASSWORD_RULES.map((r) => ({ id: r.id, label: r.label, ok: r.test(password) }));
  if (confirm !== undefined) rows.push({ id: 'match', label: 'Both passwords match', ok: password.length > 0 && password === confirm });
  return (
    <ul id={id} aria-label="Password rules" className="m-0 flex list-none flex-col gap-1.5 rounded-xl bg-subtle p-3.5 text-sm">
      {rows.map((r) => (
        <li key={r.id} className={`flex items-center gap-2 ${r.ok ? 'text-ok-fg' : 'text-ink-2'}`}>
          {r.ok
            ? <Check aria-hidden="true" size={16} strokeWidth={2.6} className="flex-none text-ok" />
            : <Circle aria-hidden="true" size={16} className="flex-none text-line-strong" />}
          <span>{r.label}<span className="sr-only">{r.ok ? ' (met)' : ' (not met yet)'}</span></span>
        </li>
      ))}
    </ul>
  );
}

function CodeField({ value, onChange, label = '6-digit code', error, autoFocus }: {
  value: string; onChange: (v: string) => void; label?: string; error?: string | null; autoFocus?: boolean;
}) {
  return (
    <TextField
      label={label}
      name="code"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      maxLength={6}
      autoFocus={autoFocus}
      error={error}
      className="max-w-[220px]"
      // Wider letter spacing reads well for codes.
      style={{ letterSpacing: '0.2em' }}
    />
  );
}

type View =
  | { kind: 'signIn'; notice?: string }
  | Exclude<SignInStep, { kind: 'done' }>
  | { kind: 'forgot' }
  | { kind: 'forgotConfirm' };

export function SignInPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const navigate = useNavigate();
  const [mode, setMode] = useState<'checking' | 'dev' | 'cognito'>('checking');
  const [view, setView] = useState<View>({ kind: 'signIn' });
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const c = await loadConfig();
      if (cancelled) return;
      if (c.authMode === 'dev') { setMode('dev'); return; }
      const s = await currentSession();
      if (cancelled) return;
      if (s && isPlatformAdmin(s)) { navigate(next, { replace: true }); return; }
      setMode('cognito');
    })();
    return () => { cancelled = true; };
  }, [navigate, next]);

  const go = (v: View) => { setError(null); setView(v); };

  /** Runs one step; 'done' leaves for the app, anything else shows the next form. */
  const step = async (fn: () => Promise<SignInStep>) => {
    setBusy(true);
    setError(null);
    try {
      const s = await fn();
      if (s.kind === 'done') { navigate(next, { replace: true }); return; }
      setView(s);
    } catch (e) {
      const err = e instanceof AuthError ? e : null;
      if (err?.code === 'session_expired' || err?.code === 'temp_expired' || err?.code === 'unsupported') {
        setView({ kind: 'signIn' });
      }
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'dev') return <Navigate to={next} replace />;
  if (mode === 'checking') return <FirmPage><Spinner label="Loading" /></FirmPage>;

  switch (view.kind) {
    case 'signIn':
      return (
        <AuthCard title="Sign in" caption="Vertekal Platform Manager is for Vertekal staff.">
          {view.notice && <Banner tone="success" role="status">{view.notice}</Banner>}
          <ErrorBanner error={error} />
          <PasswordSignIn
            email={email}
            setEmail={setEmail}
            busy={busy}
            onSubmit={(pw) => step(() => startSignIn(email, pw))}
            onForgot={() => go({ kind: 'forgot' })}
          />
        </AuthCard>
      );
    case 'newPassword':
      return (
        <AuthCard title="Choose a new password" caption="You signed in with a temporary password. Choose the password you’ll use from now on.">
          {view.notice && <Banner tone="info" role="status">{view.notice}</Banner>}
          <ErrorBanner error={error} />
          <NewPasswordForm busy={busy} submitLabel="Save password" onSubmit={(pw) => step(() => submitNewPassword(pw))} />
        </AuthCard>
      );
    case 'mfaSetup':
      return (
        <AuthCard wide title="Set up your authenticator app" caption="Every sign-in needs a code from an authenticator app. This takes about a minute.">
          {view.notice && <Banner tone="info" role="status">{view.notice}</Banner>}
          <ErrorBanner error={error} />
          <MfaSetup secret={view.secret} otpauthUrl={view.otpauthUrl} busy={busy} onSubmit={(code) => step(() => submitMfaSetupCode(code))} />
        </AuthCard>
      );
    case 'totp':
      return (
        <AuthCard title="Enter your authenticator code" caption="Open your authenticator app and enter the 6-digit code for Vertekal Platform Manager.">
          {view.notice && <Banner tone="info" role="status">{view.notice}</Banner>}
          <ErrorBanner error={error} />
          <TotpForm busy={busy} onSubmit={(code) => step(() => submitTotp(code))} onBack={() => go({ kind: 'signIn' })} />
        </AuthCard>
      );
    case 'forgot':
      return (
        <AuthCard title="Reset your password" caption="Enter your work email and we’ll send you a code.">
          <ErrorBanner error={error} />
          <ForgotRequest
            email={email}
            setEmail={setEmail}
            busy={busy}
            onSubmit={async () => {
              setBusy(true); setError(null);
              try { await requestPasswordReset(email); go({ kind: 'forgotConfirm' }); }
              catch (e) { setError(message(e)); }
              finally { setBusy(false); }
            }}
            onBack={() => go({ kind: 'signIn' })}
          />
        </AuthCard>
      );
    case 'forgotConfirm':
      return (
        <AuthCard title="Check your email" caption={<>If an account uses <strong className="[overflow-wrap:anywhere]">{email.trim()}</strong>, we sent it a 6-digit code. Enter it with your new password.</>}>
          <ErrorBanner error={error} />
          <ForgotConfirm
            busy={busy}
            onSubmit={async (code, pw) => {
              setBusy(true); setError(null);
              try { await confirmPasswordReset(email, code, pw); go({ kind: 'signIn', notice: 'Password changed. Sign in with your new password.' }); }
              catch (e) { setError(message(e)); }
              finally { setBusy(false); }
            }}
            onResend={async () => {
              setBusy(true); setError(null);
              try { await requestPasswordReset(email); } catch (e) { setError(message(e)); } finally { setBusy(false); }
            }}
            onBack={() => go({ kind: 'signIn' })}
          />
        </AuthCard>
      );
  }
}

function PasswordSignIn({ email, setEmail, busy, onSubmit, onForgot }: {
  email: string; setEmail: (v: string) => void; busy: boolean; onSubmit: (password: string) => void; onForgot: () => void;
}) {
  const [password, setPassword] = useState('');
  const [missing, setMissing] = useState<{ email?: string; password?: string }>({});
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const m = { email: email.trim() ? undefined : 'Enter your email.', password: password ? undefined : 'Enter your password.' };
    setMissing(m);
    if (m.email || m.password) return;
    onSubmit(password);
  };
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <TextField label="Email" type="email" name="username" autoComplete="username" inputMode="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} error={missing.email} />
      <PasswordField label="Password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={missing.password} />
      <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full">{busy ? 'Signing in…' : 'Sign in'}</Button>
      <div className="text-center"><Button variant="link" size="sm" onClick={onForgot}>Forgot password?</Button></div>
    </form>
  );
}

function NewPasswordForm({ busy, submitLabel, onSubmit, children }: {
  busy: boolean; submitLabel: string; onSubmit: (pw: string) => void; children?: ReactNode;
}) {
  const rulesId = useId();
  const pwId = useId();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errs, setErrs] = useState<{ pw?: string; confirm?: string }>({});
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = {
      pw: passwordMeetsPolicy(pw) ? undefined : 'Meet every rule below.',
      confirm: pw === confirm ? undefined : 'Passwords don’t match.',
    };
    setErrs(next);
    if (next.pw || next.confirm) return;
    onSubmit(pw);
  };
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      {children}
      <PasswordField id={pwId} label="New password" name="new-password" autoComplete="new-password" autoFocus={!children} value={pw} onChange={(e) => setPw(e.target.value)} error={errs.pw} aria-describedby={errs.pw ? `${pwId}-err ${rulesId}` : rulesId} />
      <PasswordField label="Confirm new password" name="confirm-password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} error={errs.confirm} />
      <PasswordRules id={rulesId} password={pw} confirm={confirm} />
      <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full">{busy ? 'Saving…' : submitLabel}</Button>
    </form>
  );
}

/** Groups a base32 secret in fours so it can be typed by hand. */
export const groupSecret = (s: string) => s.replace(/\s+/g, '').match(/.{1,4}/g)?.join(' ') ?? s;

function MfaSetup({ secret, otpauthUrl, busy, onSubmit }: { secret: string; otpauthUrl: string; busy: boolean; onSubmit: (code: string) => void }) {
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<'idle' | 'ok' | 'fail'>('idle');
  useEffect(() => { setCode(''); setCopied('idle'); }, [secret]);
  const copy = async () => {
    try { await navigator.clipboard.writeText(secret); setCopied('ok'); } catch { setCopied('fail'); }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) { setCodeErr('Enter the 6-digit code from your app.'); return; }
    setCodeErr(null);
    onSubmit(code);
  };
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <ol className="m-0 flex list-none flex-col gap-5 p-0">
        <li>
          <div className="mb-1 font-semibold">1. Open your authenticator app</div>
          <div className="text-ink-2">Microsoft Authenticator, Google Authenticator, 1Password or any app that supports time-based codes. Add a new account.</div>
        </li>
        <li>
          <div className="mb-2 font-semibold">2. Scan this QR code</div>
          <div className="inline-block rounded-xl border border-line bg-white p-3">
            <QRCodeSVG value={otpauthUrl} size={176} marginSize={0} title="QR code for your authenticator app" />
          </div>
          <div className="mt-3 text-sm text-ink-2">Can’t scan it? Enter this key instead:</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <code data-testid="totp-secret" className="rounded-lg bg-subtle px-3 py-2 font-mono text-[15px] tracking-[0.04em] text-ink [overflow-wrap:anywhere]">{groupSecret(secret)}</code>
            <Button size="sm" onClick={() => void copy()}><Copy aria-hidden="true" size={16} />Copy key</Button>
          </div>
          <div role="status" aria-live="polite" className="mt-1 min-h-[20px] text-[13px] text-muted">
            {copied === 'ok' ? 'Key copied.' : copied === 'fail' ? 'Couldn’t copy. Select the key and copy it.' : ''}
          </div>
        </li>
        <li>
          <div className="mb-2 font-semibold">3. Enter the code your app shows</div>
          <CodeField value={code} onChange={setCode} error={codeErr} />
        </li>
      </ol>
      <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full">{busy ? 'Checking…' : 'Verify and sign in'}</Button>
    </form>
  );
}

function TotpForm({ busy, onSubmit, onBack }: { busy: boolean; onSubmit: (code: string) => void; onBack: () => void }) {
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) { setCodeErr('Enter the 6-digit code from your app.'); return; }
    setCodeErr(null);
    onSubmit(code);
  };
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <CodeField value={code} onChange={setCode} error={codeErr} autoFocus />
      <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full">{busy ? 'Checking…' : 'Sign in'}</Button>
      <div className="text-center"><Button variant="link" size="sm" onClick={onBack}>Use a different account</Button></div>
    </form>
  );
}

function ForgotRequest({ email, setEmail, busy, onSubmit, onBack }: {
  email: string; setEmail: (v: string) => void; busy: boolean; onSubmit: () => void; onBack: () => void;
}) {
  const [err, setErr] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setErr('Enter your email.'); return; }
    setErr(null);
    onSubmit();
  };
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <TextField label="Email" type="email" name="username" autoComplete="username" inputMode="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} error={err} />
      <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full">{busy ? 'Sending…' : 'Send code'}</Button>
      <div className="text-center"><Button variant="link" size="sm" onClick={onBack}>Back to sign in</Button></div>
    </form>
  );
}

function ForgotConfirm({ busy, onSubmit, onResend, onBack }: {
  busy: boolean; onSubmit: (code: string, pw: string) => void; onResend: () => void; onBack: () => void;
}) {
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState<string | null>(null);
  return (
    <>
      <NewPasswordForm
        busy={busy}
        submitLabel="Set new password"
        onSubmit={(pw) => {
          if (code.length !== 6) { setCodeErr('Enter the 6-digit code from the email.'); return; }
          setCodeErr(null);
          onSubmit(code, pw);
        }}
      >
        <CodeField label="Code from the email" value={code} onChange={setCode} error={codeErr} autoFocus />
      </NewPasswordForm>
      <div className="flex flex-wrap justify-center gap-x-6">
        <Button variant="link" size="sm" disabled={busy} onClick={onResend}>Send a new code</Button>
        <Button variant="link" size="sm" onClick={onBack}>Back to sign in</Button>
      </div>
    </>
  );
}

/** Signed in, but the token has no platform-admin group. */
export function NoAccess({ email }: { email: string }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  return (
    <AuthCard title="Your account doesn’t have access">
      <p className="m-0 text-ink-2">
        You’re signed in as <strong className="[overflow-wrap:anywhere]">{email}</strong>, but this account isn’t set up for Vertekal Platform Manager.
        Ask an admin to add you to the platform-admin group, then sign in again.
      </p>
      <div>
        <Button variant="primary" size="lg" disabled={busy} onClick={async () => { setBusy(true); await signOut(); navigate('/admin/sign-in', { replace: true }); }}>
          {busy ? 'Signing out…' : 'Sign out'}
        </Button>
      </div>
    </AuthCard>
  );
}
